import React, { useEffect, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { firestore } from '../firebase';
import { trackBlogView, trackPageView, trackLinkClick } from '../utils/tracking';

const DynamicBlogPost = () => {
  const { slug } = useParams();
  const history = useHistory();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!slug) {
      setError(true);
      setLoading(false);
      return;
    }

    const unsubscribe = firestore
      .collection('blogs')
      .where('slug', '==', slug)
      .where('published', '==', true)
      .limit(1)
      .onSnapshot(
        (snapshot) => {
          if (snapshot.empty) {
            setError(true);
            setLoading(false);
            return;
          }

          const doc = snapshot.docs[0];
          const data = doc.data();
          
          const blogData = {
            id: doc.id,
            title: data.title,
            subtitle: data.subtitle,
            body: data.body,
            heroImage: data.heroImage,
            author: data.author || 'Lunar Electric',
            createdAt: data.createdAt,
            sections: data.sections || [],
            slug: data.slug
          };

          setBlog(blogData);
          setLoading(false);

          // Track page view
          trackBlogView(slug, blogData.title);
          trackPageView(`/blogs/${slug}`, blogData.title);

          // Update page title
          document.title = `${blogData.title} | Lunar Electric`;
        },
        (error) => {
          console.error('Error fetching blog:', error);
          setError(true);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, [slug]);

  // Fetch related blogs (all published blogs except current one)
  useEffect(() => {
    if (!slug) return;

    // Hardcoded existing blogs
    const hardcodedBlogs = [
      {
        title: "7 Hidden Costs of NOT Going Solar in 2026 (Beyond Electricity Bills)",
        excerpt: "Discover 7 hidden costs of not going solar in 2026: productivity losses, diesel generator costs, carbon taxes, competitive disadvantage, ESG compliance pressure, rising tariffs, and higher insurance costs. Learn why delaying solar costs more than installing it today.",
        slug: "hidden-costs-not-going-solar-2026"
      },
      {
        title: "Solar EMI Financing for MSMEs: Complete guide to zero upfront cost in 2025",
        excerpt: "Complete guide to solar EMI financing for MSMEs in India. Learn about zero upfront cost solar installations, loan eligibility, benefits, ROI, and how to get started with solar energy for your business.",
        slug: "msme-solar-emi-financing"
      },
      {
        title: "Commercial Solar Depreciation & Tax Benefits in India",
        excerpt: "Break down accelerated depreciation, MAT planning, GST input credits, and compliance steps Indian businesses should know before installing commercial solar.",
        slug: "commercial-solar-tax-benefits"
      },
      {
        title: "MC4 Connector Guide: What They Are, Why They Matter & How to Choose",
        excerpt: "Complete guide to MC4 connectors for solar panels. Learn what MC4 connectors are, why they're important for residential and commercial solar systems, and how to choose the right ones.",
        slug: "mc4-connector-guide"
      },
      {
        title: "Top 10 Benefits of Solar Panels for Indian Homes",
        excerpt: "Discover the top 10 benefits of installing solar panels for Indian homes. Learn about cost savings, government subsidies, environmental benefits, and more.",
        slug: "solar-panel-benefits"
      },
      {
        title: "5 Solar Procurement Models for Indian Businesses: CAPEX vs OPEX Guide",
        excerpt: "Complete guide to 5 solar procurement models for Indian businesses: CAPEX, OPEX, RESCO, Group Captive, and Third Party PPA. Choose the right model for your needs.",
        slug: "solar-models-explained"
      },
      {
        title: "Why Businesses Should Adopt Solar in India: Complete Guide",
        excerpt: "Discover why businesses should adopt solar energy in India. Learn about cost savings, government incentives, tax benefits, and ROI for commercial solar installations.",
        slug: "commercial-solar-benefits"
      },
      {
        title: "Rooftop Solar in India: A Simple, Practical Guide for Homeowners",
        excerpt: "Complete step-by-step guide to installing rooftop solar at home. Learn about sizing, ALMM compliance, net metering, PM Surya Ghar subsidy, and more.",
        slug: "rooftop-solar-guide"
      }
    ];

    // Fetch published blogs from Firestore
    const unsubscribe = firestore
      .collection('blogs')
      .where('published', '==', true)
      .onSnapshot(
        (snapshot) => {
          const firestoreBlogs = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              title: data.title,
              excerpt: data.excerpt || data.subtitle || '',
              slug: data.slug
            };
          });

          // Combine hardcoded and Firestore blogs, exclude current blog
          // Remove duplicates by slug
          const allBlogsMap = new Map();
          [...firestoreBlogs, ...hardcodedBlogs].forEach(blog => {
            if (blog.slug && blog.slug !== slug && !allBlogsMap.has(blog.slug)) {
              allBlogsMap.set(blog.slug, blog);
            }
          });
          
          // Randomize the order of blogs
          const allBlogsArray = Array.from(allBlogsMap.values());
          // Shuffle array using Fisher-Yates algorithm
          for (let i = allBlogsArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [allBlogsArray[i], allBlogsArray[j]] = [allBlogsArray[j], allBlogsArray[i]];
          }
          
          const allBlogs = allBlogsArray.slice(0, 2); // Show 2 related blogs

          setRelatedBlogs(allBlogs);
        },
        (error) => {
          console.error('Error fetching related blogs:', error);
          // Fallback to hardcoded blogs only
          const filteredBlogs = hardcodedBlogs.filter(b => b.slug !== slug);
          // Randomize the order
          for (let i = filteredBlogs.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [filteredBlogs[i], filteredBlogs[j]] = [filteredBlogs[j], filteredBlogs[i]];
          }
          const fallbackBlogs = filteredBlogs.slice(0, 2);
          setRelatedBlogs(fallbackBlogs);
        }
      );

    return () => unsubscribe();
  }, [slug]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryFromSections = (sections) => {
    if (!sections || sections.length === 0) return 'Solar Benefits';
    
    if (sections.includes('commercial')) {
      return 'Commercial Solar';
    }
    if (sections.includes('residential')) {
      return 'Residential Rooftop Solar';
    }
    if (sections.includes('industrial')) {
      return 'Industrial Solar';
    }
    return 'Solar Benefits';
  };

  const calculateReadTime = (content) => {
    if (!content) return '5';
    // Remove HTML tags and count words
    const text = content.replace(/<[^>]*>/g, '').trim();
    const wordCount = text.split(/\s+/).length;
    // Average reading speed: 200 words per minute
    const readTime = Math.ceil(wordCount / 200);
    return readTime || '5';
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    // Don't truncate if text fits - let CSS handle overflow with ellipsis
    return text;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="blog-post-page" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <h2>Loading blog post...</h2>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Navbar />
        <div className="blog-post-page" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <h2>Blog post not found</h2>
            <p>The blog post you're looking for doesn't exist or has been removed.</p>
            <Link to="/blogs" className="btn btn-primary" style={{ marginTop: '1rem' }}>
              Back to Blogs
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = getCategoryFromSections(blog.sections);
  const readTime = calculateReadTime(blog.body);
  const publishedDate = formatDate(blog.createdAt);

  return (
    <>
      <Helmet>
        <title>{blog.title} | Lunar Electric</title>
        <meta name="description" content={blog.subtitle || blog.title} />
        <meta name="keywords" content={`solar, solar energy, ${blog.title}, lunar electric`} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.subtitle || blog.title} />
        <meta property="og:image" content={blog.heroImage || 'https://lunarelectric.in/logo512.png'} />
        <meta property="og:url" content={`https://lunarelectric.in/blogs/${blog.slug}`} />
        <link rel="canonical" href={`https://lunarelectric.in/blogs/${blog.slug}`} />
      </Helmet>
      <Navbar />
      <a href="#main-content" className="skip-to-main" style={{position: 'absolute', left: '-9999px', zIndex: 9999}} onFocus={(e) => e.target.style.left = '0'}>Skip to main content</a>
      <div className="blog-post-page" id="main-content">
        {/* Blog Header */}
        <div className="blog-hero">
          {blog.heroImage && (
            <img 
              src={blog.heroImage} 
              alt={blog.title}
              title={blog.title}
              loading="eager"
              width="1200"
              height="630"
            />
          )}
          <div className="blog-overlay">
            <div className="container">
              <h1>{blog.title}</h1>
              <div className="blog-meta">
                <span>Published: {publishedDate}</span>
                <span>Category: {category}</span>
                <span>⏱️ {readTime} min read</span>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="blog-breadcrumb">
          <div className="container">
            <Link to="/" aria-label="Home">Home</Link> / <Link to="/blogs" aria-label="Blogs">Blogs</Link> / <span aria-current="page">{blog.title}</span>
          </div>
        </nav>

        {/* Blog Content */}
        <div className="blog-content-wrapper">
          <div className="container">
            <div className="blog-article">
              {blog.subtitle && (
                <div className="blog-intro">
                  {blog.subtitle}
                </div>
              )}
              <div 
                dangerouslySetInnerHTML={{ __html: blog.body }}
                className="blog-article-content"
              />
              <style>{`
                .blog-intro {
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-intro p {
                  font-family: 'Poppins', sans-serif !important;
                  font-size: 18px !important;
                  line-height: 1.8 !important;
                  color: #4a5568 !important;
                }
                .blog-article-content {
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-article-content p {
                  font-size: 16px !important;
                  line-height: 1.8 !important;
                  color: #4a5568 !important;
                  margin-bottom: 1.25rem !important;
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-article-content h2 {
                  font-size: 28px !important;
                  font-weight: 700 !important;
                  color: #1F3A93 !important;
                  margin-top: 2.5rem !important;
                  margin-bottom: 1.5rem !important;
                  line-height: 1.3 !important;
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-article-content h3 {
                  font-size: 24px !important;
                  font-weight: 600 !important;
                  color: #1F3A93 !important;
                  margin-top: 2rem !important;
                  margin-bottom: 1rem !important;
                  line-height: 1.3 !important;
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-article-content h4 {
                  font-size: 20px !important;
                  font-weight: 600 !important;
                  color: #1F3A93 !important;
                  margin-top: 1.5rem !important;
                  margin-bottom: 0.75rem !important;
                  line-height: 1.3 !important;
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-article-content ul,
                .blog-article-content ol {
                  margin: 1.5rem 0 !important;
                  padding-left: 2rem !important;
                }
                .blog-article-content li {
                  font-size: 16px !important;
                  line-height: 1.8 !important;
                  color: #4a5568 !important;
                  margin-bottom: 0.75rem !important;
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-article-content strong {
                  font-weight: 600 !important;
                  color: #1e293b !important;
                }
                .blog-article-content * {
                  font-family: 'Poppins', sans-serif !important;
                }
                /* Override any external styles from pasted content */
                .blog-article-content [style*="font-family"] {
                  font-family: 'Poppins', sans-serif !important;
                }
                .blog-article-content [style*="font-size"] {
                  font-size: 16px !important;
                }
                .blog-article-content [style*="color"]:not([style*="background"]) {
                  color: #4a5568 !important;
                }
              `}</style>

              {/* Related Articles Section */}
              {relatedBlogs.length > 0 && (
                <div className="blog-related-posts">
                  <h3>Related Articles</h3>
                  <div className="related-posts-grid">
                    {relatedBlogs.map((relatedBlog, index) => (
                      <Link 
                        key={index}
                        to={`/blogs/${relatedBlog.slug}`} 
                        className="related-post-card"
                        onClick={() => trackLinkClick(relatedBlog.title, `/blogs/${relatedBlog.slug}`, 'internal')}
                      >
                        <h4>{relatedBlog.title}</h4>
                        <p>{relatedBlog.excerpt}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Blog CTA Box */}
        <div className="blog-cta-box">
          <div className="container">
            <h2>Ready to Go Solar?</h2>
            <p>Get in touch for professional solar rooftop installations for your home or business</p>
            <div>
              <Link 
                to="/callback" 
                className="btn btn-primary"
                onClick={() => trackLinkClick('Get Callback', '/callback', 'internal')}
              >
                Get Callback
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DynamicBlogPost;


